import * as React from 'react';
import styled from 'styled-components';
import cl from 'classnames';

import emojiList from '@doltech/utils/lib/emoji-list';
import { numberToRoman } from '@doltech/utils/lib/text';

import { IconSystemV2ArrowDropdownArrowRight } from '@doltech/icons/SystemV2/IconSystemV2ArrowDropdownArrowRight';
import { colorsV2 } from '../../colors-v2';
import { ResponsiveTypography } from '../../Typography/v3/ResponsiveTypography';
import { ButtonIconSecondary } from '../../Button/ButtonIconSecondary/ButtonIconSecondary';

const Dot = styled.div`
  width: 4px;
  min-width: 4px;
  height: 4px;

  background-color: ${colorsV2.neutral100};
  border-radius: 50%;
`;

const IconEmoji = styled.div``;

const Container = styled.div`
  display: flex;
  align-items: center;

  min-width: 24px;
  height: 20px;

  .order-number {
    color: ${colorsV2.neutral100};
  }

  &.active {
    .order-number {
      color: ${colorsV2.primary100};
    }
  }

  &.arrow {
    svg {
      transition: all 0.15s ease-out;
    }

    &.expanded {
      svg {
        transform: rotate(90deg);
      }
    }
  }
`;

interface ElementTableOfContentHeadingBulletProps {
  type?: 'icon' | 'roman-numeral' | 'latin-number' | 'dot' | 'arrow-collapse' | 'arrow-expand';
  order?: number // start from 1
  active?: boolean;
}

export const ElementTableOfContentHeadingBullet = (props: ElementTableOfContentHeadingBulletProps) => {
  const { type, order, active = false } = props;

  const selectedEmojiList = [
    'fire',
    'star2',
    'blush',
    'zap',
    'palm_tree',
    'sparkles',
    'tada',
    'cactus',
    'computer',
    'bulb',
    'book',
    'shamrock',
    'football',
    'books',
    'microscope',
    'balloon',
    'desktop_computer',
    'four_leaf_clover',
    'dart',
  ];

  const iconEmojiList = emojiList
    .filter((eachEmoji) => selectedEmojiList.includes(eachEmoji.aliases?.[0]))
    .sort((a, b) => selectedEmojiList.indexOf(a.aliases?.[0]) - selectedEmojiList.indexOf(b.aliases?.[0]));

  switch (type) {
    case 'arrow-expand':
    case 'arrow-collapse':
      return (
        <Container className={cl('element-table-of-content-heading-bullet arrow', { active, expanded: type === 'arrow-expand' })}>
          <ButtonIconSecondary
            size="xs"
            buttonStyle="gray1"
            shape="square"
            icon={<IconSystemV2ArrowDropdownArrowRight />}
            transparent
          />
        </Container>
      );
    case 'dot':
      return (
        <Container className={cl('element-table-of-content-heading-bullet dot', { active })}>
          <Dot />
        </Container>
      );
    case 'latin-number':
      return (
        <Container className={cl('element-table-of-content-heading-bullet latin-number', { active })}>
          <ResponsiveTypography.Paragraph className="order-number" variant="semi-bold/14-20">
            {order}.
          </ResponsiveTypography.Paragraph>
        </Container>
      );
    case 'roman-numeral':
      return (
        <Container className={cl('element-table-of-content-heading-bullet roman-numeral', { active })}>
          <ResponsiveTypography.Paragraph className="order-number" variant="semi-bold/14-20">
            {numberToRoman(order)}.
          </ResponsiveTypography.Paragraph>
        </Container>
      );
    case 'icon':
    default:
  }

  return (
    <Container className={cl('element-table-of-content-heading-bullet icon', { active })}>
      <IconEmoji>
        {iconEmojiList[((order - 1) % iconEmojiList.length)]?.emoji}
      </IconEmoji>
    </Container>
  );
};
